import type { FC } from 'react';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineConfirmationNumber } from 'react-icons/md';
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import type { RootCausesData } from 'src/types/followUpStatistics';
import { getCurrentLocale } from 'src/helper/helper';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import HorizontalBarChart from 'src/components/followUp/common/BaseWidgets/HorizontalBarChart/HorizontalBarChart';
import Pill from 'src/components/followUp/common/Pill/Pill';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { useRootCauseExportOptions } from './hooks';
import { buildRootCauseData } from './mapper';

export const ROOT_CAUSE_WIDGET_LABEL_TEST_ID = 'root-cause-widget-label';

const RootCauseWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const theme = useTheme();
  const { t } = useTranslation('followUp');
  const locale = getCurrentLocale();
  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.AGGREGATE_LIST,
    config.id,
  );

  const { data, errorMsg, refetch, isFetching, queryFilters } =
    useWidgetQuery<RootCausesData>(endpoint);

  const rootCauseData = useMemo(
    () => buildRootCauseData({ data, locale, t }),
    [data, locale, t],
  );
  const hasData = data != null;

  const exportOptions = useRootCauseExportOptions({
    data: hasData ? rootCauseData : null,
    widgetName: config.name,
    queryFilters,
  });

  const { bars, total } = rootCauseData;
  const totalLabel = total.toLocaleString(locale);
  const totalAriaLabel = t('statistics.rootCausesWidget.totalFollowUps', {
    count: total,
    defaultValue: 'Total Follow-Ups: {{count}}',
  });

  const hasResults = bars.length > 0;

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      error={errorMsg}
      loading={isFetching}
      maxHeight="auto"
      exportOptions={exportOptions}
      header={
        <Stack gap={0.5}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h5" id={ariaId}>
              {config.name}
            </Typography>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Tooltip title={totalAriaLabel}>
              <Box component="span" aria-label={totalAriaLabel}>
                <Pill
                  label={
                    <Typography variant="caption" fontWeight={600}>
                      {totalLabel}
                    </Typography>
                  }
                  icon={<MdOutlineConfirmationNumber />}
                  color="primary"
                />
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      }
      footer={
        hasResults ? (
          <Box p="1rem 1.5rem">
            <Typography variant="body3" fontWeight={600}>
              {t('multipleChoice')}
            </Typography>
          </Box>
        ) : (
          <NoResultsToShowAdjustFiltersWarning />
        )
      }
    >
      {hasData ? (
        <Stack width="100%" spacing={1.5}>
          <HorizontalBarChart
            bars={bars.map((bar) => ({
              label: bar.label,
              value: bar.count,
              relativeValue: Math.min(bar.percentage, 100),
              pillLabel: (
                <Typography
                  variant="caption2"
                  color={theme.colors.secondary[800]}
                  fontWeight={600}
                  data-test-id={ROOT_CAUSE_WIDGET_LABEL_TEST_ID}
                >
                  {bar.pillLabel}
                </Typography>
              ),
              tooltip: bar.tooltip,
            }))}
            noDataAvailable={!hasResults}
          />
        </Stack>
      ) : null}
    </WidgetFrame>
  );
};

export default RootCauseWidget;
